// RSchema: the spec for one table group, as a standalone RObject in its own
// DAG.
//
// ACTIONS (see payload.ts for formats):
//
//   create
//     Genesis of a schema. Carries: name,
//     `creators` (keyId + publicKey pairs; spec authority — they may sign
//     schema-updates), the initial TableDef[] (columns with types /
//     nullable / default / pub, concurrentDeletes, fks: column -> table with
//     at-use semantics, restrictions: at-use predicates tagged
//     insert / update / delete / all), hash algorithm. No object-op gates:
//     deploy authority is per-instance policy (RTableGroup's canDeploy).
//
//   schema-update
//     Evolves the spec. Carries ONLY the migration rules — the slot writes:
//     add/drop-table, add/drop-column, set-concurrent-deletes,
//     set-fks, set-restrictions. No resulting defs and no result
//     hash: the effective schema is derived by the per-slot LWW resolution
//     and never serialized. Plus an optional note, and a REQUIRED author +
//     signature from one of the creators. Deploying an update is a separate
//     act: each observing RTableGroup barrier-ref-advances to the new
//     RSchema version at its own pace; concurrent deploys resolve by union
//     (the effective schema is the resolution at the merged version).
//
// The DAG has no barriers, so the effective schema is a pure function of the
// position `at` (see resolve.ts); resolved states are cached per normalized
// position. Schema DAGs are small: ops carry no entry meta and resolution
// does a full scan.
//
// RSchema does not sync on its own (no startSync): the RDb orchestrates
// sync for its members later if needed.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
var _a;
import { HASH_SHA256 } from "@hyper-hyper-space/hhs3_crypto";
import { dag, position } from "@hyper-hyper-space/hhs3_dag";
import { formatValidationFailure, ValidationRejectedError, } from "@hyper-hyper-space/hhs3_mvt";
import { RootScopedDag } from "@hyper-hyper-space/hhs3_mvt";
import { signPayload as signPayloadHelper, serializePublicKeyToBase64 } from "@hyper-hyper-space/hhs3_mvt";
import { RSCHEMA_TYPE_ID } from "./payload.js";
import { validateRSchemaPayload } from "./validate_ops.js";
import { resolveSchemaState, positionKey } from "./resolve.js";
import { RSchemaViewImpl } from "./view.js";
import { RSchemaDeltaAccumulator, computeRSchemaDelta } from "./delta.js";
export { RSCHEMA_TYPE_ID } from "./payload.js";
export const rSchemaFactory = {
    computeRootObjectId: (payload, ctx) => __awaiter(void 0, void 0, void 0, function* () {
        const entry = dag.createEntry(payload, {}, position(), ctx.getCrypto().hash(HASH_SHA256));
        return entry.hash;
    }),
    validateCreationPayload: (payload, ctx) => __awaiter(void 0, void 0, void 0, function* () { return validateRSchemaPayload(payload, { mode: 'create', ctx }); }),
    executeCreationPayload: (payload, _ctx, scopedDag) => __awaiter(void 0, void 0, void 0, function* () {
        return yield scopedDag.append(payload, {}, position());
    }),
    loadObject: (id, ctx, opts) => __awaiter(void 0, void 0, void 0, function* () {
        var _b;
        if ((opts === null || opts === void 0 ? void 0 : opts.parent) !== undefined) {
            throw new Error("RSchema is a standalone object (no nesting parent)");
        }
        const backendLabel = (_b = opts === null || opts === void 0 ? void 0 : opts.backendLabel) !== null && _b !== void 0 ? _b : 'default';
        const rawDag = yield ctx.getDag(id, backendLabel);
        if (rawDag === undefined)
            throw new Error(`DAG '${id}' not found`);
        const scopedDag = new RootScopedDag(rawDag);
        const createOp = (yield scopedDag.loadEntry(id)).payload;
        return new RSchemaImpl(id, createOp, ctx, backendLabel);
    }),
};
export class RSchemaImpl {
    constructor(createOpId, createOp, ctx, backendLabel = 'default') {
        // resolved states are immutable per position; keyed by normalized `at`
        this.resolveCache = new Map();
        this.createOpId = createOpId;
        this.createOp = createOp;
        this.ctx = ctx;
        this.backendLabel = backendLabel;
    }
    getId() { return this.createOpId; }
    getType() { return _a.typeId; }
    getBackendLabel() { return this.backendLabel; }
    getName() { return this.createOp.name; }
    hashAlgorithm() { return this.createOp.hashAlgorithm; }
    getContext() { return this.ctx; }
    selfValidate() {
        return this.ctx.getConfig().selfValidate || false;
    }
    // The only writer beyond creation: build a rules-only schema-update,
    // sign it and append it at `at` (defaults to the current frontier).
    updateSchema(migration, author, note, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            at = at !== null && at !== void 0 ? at : yield scopedDag.getFrontier();
            const base = {
                action: 'schema-update',
                migration,
            };
            if (note !== undefined)
                base.note = note;
            const signed = yield signPayloadHelper(base, author);
            if (this.selfValidate()) {
                const result = yield this.validatePayload(signed, at);
                if (!result.valid) {
                    throw new ValidationRejectedError(formatValidationFailure(result.why), result.why);
                }
            }
            return this.applyPayload(signed, at);
        });
    }
    // RObject interface
    validatePayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            return validateRSchemaPayload(payload, { mode: 'op', schema: this, at });
        });
    }
    applyPayload(payload, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            return yield scopedDag.append(payload, {}, at);
        });
    }
    getView(at, from) {
        return __awaiter(this, void 0, void 0, function* () {
            const scopedDag = yield this.getScopedDag();
            at = at !== null && at !== void 0 ? at : yield scopedDag.getFrontier();
            from = from !== null && from !== void 0 ? from : yield scopedDag.getFrontier();
            const state = yield this.resolveAt(at);
            return new RSchemaViewImpl(this, state, at, from);
        });
    }
    // Resolve the effective schema at `at`, with a per-version cache (the
    // resolution ignores the view horizon, so the result is immutable).
    resolveAt(at) {
        return __awaiter(this, void 0, void 0, function* () {
            var _b, e_1, _c, _d;
            const scopedDag = yield this.getScopedDag();
            const normalized = yield scopedDag.findMinimalCover(at);
            const key = positionKey(normalized);
            const cached = this.resolveCache.get(key);
            if (cached !== undefined)
                return cached;
            const entries = [];
            try {
                for (var _e = true, _f = __asyncValues(scopedDag.loadAllEntries()), _g; _g = yield _f.next(), _b = _g.done, !_b; _e = true) {
                    _d = _g.value;
                    _e = false;
                    const entry = _d;
                    entries.push(entry);
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (!_e && !_b && (_c = _f.return)) yield _c.call(_f);
                }
                finally { if (e_1) throw e_1.error; }
            }
            const state = resolveSchemaState(entries, normalized);
            this.resolveCache.set(key, state);
            return state;
        });
    }
    extractForeignDeps(_payload, _at) {
        return undefined;
    }
    computeDelta(start, end) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawDag = yield this.ctx.getDag(this.createOpId, this.backendLabel);
            if (rawDag === undefined)
                throw new Error(`DAG '${this.createOpId}' not found`);
            return computeRSchemaDelta(this, rawDag, start, end);
        });
    }
    createDeltaAccumulator(start, end) {
        return new RSchemaDeltaAccumulator(this, start, end);
    }
    subscribe(_callback) {
        throw new Error("Method not implemented.");
    }
    unsubscribe(_callback) {
        throw new Error("Method not implemented.");
    }
    getScopedDag() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._scopedDag === undefined) {
                const rawDag = yield this.ctx.getDag(this.createOpId, this.backendLabel);
                if (rawDag === undefined)
                    throw new Error(`DAG '${this.createOpId}' not found`);
                this._scopedDag = new RootScopedDag(rawDag);
            }
            return this._scopedDag;
        });
    }
    getCausalDag() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._causalDag === undefined) {
                const rawDag = yield this.ctx.getDag(this.createOpId, this.backendLabel);
                if (rawDag === undefined)
                    throw new Error(`DAG '${this.createOpId}' not found`);
                this._causalDag = rawDag;
            }
            return this._causalDag;
        });
    }
    destroy() {
        return __awaiter(this, void 0, void 0, function* () {
            this._scopedDag = undefined;
            this._causalDag = undefined;
            this.resolveCache.clear();
        });
    }
}
_a = RSchemaImpl;
RSchemaImpl.create = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const createPayload = {
        action: 'create',
        type: RSCHEMA_TYPE_ID,
        name: options.name,
        creators: options.creators.map((c) => ({
            keyId: c.keyId,
            publicKey: serializePublicKeyToBase64(c.publicKey),
        })),
        tables: options.tables,
    };
    if (options.hashAlgorithm !== undefined)
        createPayload.hashAlgorithm = options.hashAlgorithm;
    return createPayload;
});
RSchemaImpl.typeId = RSCHEMA_TYPE_ID;
export { RSchemaViewImpl } from "./view.js";
